import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { Confirm } from '@client/components/_ui/feedback/Confirm';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import { ComplianceAuditTemplateTypeTag } from '@client/components/compliance-audit-templates/ComplianceAuditTemplateTypeTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { Archive, CircleQuestionMark, RotateCcw } from 'lucide-react';
import { ComplianceAuditTemplateFilters } from './ComplianceAuditTemplateFilters';

export const Route = createFileRoute('/admin-console/platform-content/compliance-audit-templates/search')({
    staticData: {
        presentation: 'fullPage',
        breadcrumb: { title: 'Search' },
        ui: {
            title: 'File audit templates',
            subtitle: 'Audit templates that define how a file audit runs.',
            widthVariant: 'fullWidth',
        },
    },
    component: ComplianceAuditTemplatesList,
});

type QueryParams = RouterInputs['admin']['complianceAuditTemplate']['listComplianceAuditTemplates'];
type ListData = RouterOutputs['admin']['complianceAuditTemplate']['listComplianceAuditTemplates'];
type TemplateRow = ListData['rows'][number];
type TemplateOrderBy = NonNullable<QueryParams['orderBy']>;
type TemplateWhere = NonNullable<QueryParams['where']>;

/**
 * Pull the name-search fragment (`Versions: { some: { name: { contains } } }`,
 * produced only by the search filter) out of the composed `where` and return
 * the term separately — it becomes the list input's top-level `search` param.
 */
function extractRepresentativeSearch(where: TemplateWhere): { search?: string; rest: TemplateWhere } {
    const isSearchFragment = (entry: TemplateWhere): string | undefined => {
        const name = entry.Versions?.some?.name;
        const contains = name && typeof name === 'object' && 'contains' in name ? name.contains : undefined;
        // The status filter also quantifies Versions — only the name-only shape is the search.
        return typeof contains === 'string' && Object.keys(entry).length === 1 ? contains : undefined;
    };

    const direct = isSearchFragment(where);
    if (direct !== undefined) {
        return { search: direct, rest: {} };
    }

    if (Array.isArray(where.AND)) {
        const searchEntry = where.AND.find((entry) => isSearchFragment(entry) !== undefined);
        if (searchEntry) {
            return {
                search: isSearchFragment(searchEntry),
                rest: { ...where, AND: where.AND.filter((entry) => entry !== searchEntry) },
            };
        }
    }

    return { rest: where };
}

function ComplianceAuditTemplatesList() {
    return (
        <PageController route={Route}>
            <ComplianceAuditTemplatesTable />
        </PageController>
    );
}

function ComplianceAuditTemplatesTable() {
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { formatDate } = useFormatter();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
        where: {},
    });

    const { data, isPending, error, refetch } =
        trpc.admin.complianceAuditTemplate.listComplianceAuditTemplates.useQuery(input);

    const archiveMutation = trpc.admin.complianceAuditTemplate.archiveComplianceAuditTemplate.useMutation();
    const restoreMutation = trpc.admin.complianceAuditTemplate.restoreComplianceAuditTemplate.useMutation();

    const restoreTemplate = (row: TemplateRow) => {
        modal.confirm({
            title: 'Restore template',
            content: 'Are you sure you want to restore this template? It will become editable and publishable again.',
            okText: 'Restore',
            cancelText: 'Cancel',
            onOk() {
                // duration 0: keep the toast up until the mutation settles, then
                // clear it explicitly on both paths.
                message.loading('Saving...', 0);

                restoreMutation.mutate(
                    { complianceAuditTemplateId: row.id },
                    {
                        onSuccess() {
                            message.destroy();
                            message.success('Saved.');
                            void trpcUtils.admin.complianceAuditTemplate.invalidate();
                        },
                        onError(error) {
                            message.destroy();
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const columnHelper = createColumnHelper<TemplateRow, TemplateOrderBy>();
    const columns = [
        columnHelper.display({
            id: 'name',
            header: 'Name',
            cell: ({ row }) => row.representativeVersion?.name ?? '-',
        }),
        columnHelper.display({
            id: 'version',
            header: 'Version',
            cell: ({ row }) =>
                row.representativeVersion ? <Tag variant="outlined">v{row.representativeVersion.version}</Tag> : null,
            width: 90,
        }),
        columnHelper.display({
            id: 'type',
            header: 'Type',
            cell: ({ row }) => <ComplianceAuditTemplateTypeTag value={row.type} />,
            width: 110,
        }),
        columnHelper.display({
            id: 'status',
            header: 'Status',
            cell: ({ row }) =>
                row.representativeVersion ? (
                    <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge
                            variant="outlined"
                            module="complianceAuditTemplateVersion"
                            status={row.representativeVersion.status}
                        />
                        {row.hasUnpublishedChanges && (
                            // Derived cross-version state, not an enum status — the same
                            // one-off Tag the details page shows beside the version button.
                            <Tag color="orange" variant="outlined" className="m-0">
                                Draft – published version changed
                            </Tag>
                        )}
                    </div>
                ) : null,
            width: 120,
        }),
        columnHelper.display({
            id: 'assignedOrgs',
            header: 'Assigned to',
            cell: ({ row }) =>
                row.AssignedOrgs.length ? (
                    <div className="flex flex-wrap gap-1">
                        {row.AssignedOrgs.map(({ id, name }) => (
                            <Tag key={String(id)} color="default" variant="outlined" className="me-0">
                                {name}
                            </Tag>
                        ))}
                    </div>
                ) : (
                    '-'
                ),
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created on',
            sortable: 'createdAt',
            cell: ({ row }) => formatDate(row.createdAt),
            width: 130,
        }),
        columnHelper.actions({
            id: 'actions',
            cell: ({ row }) => (
                <div className="flex gap-2">
                    <Button.OpenInNewTab
                        to="/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId"
                        params={{ complianceAuditTemplateId: row.id }}
                    />
                    {row.isArchived ? (
                        <Button.Icon
                            icon={<RotateCcw />}
                            color="cyan"
                            tooltip="Restore template"
                            onClick={() => restoreTemplate(row)}
                        />
                    ) : (
                        <Confirm
                            icon={<CircleQuestionMark className="size-3.5 text-status-danger" />}
                            title="Are you sure?"
                            okText="Archive"
                            okDanger
                            onOk={() => {
                                // duration 0: keep the toast up until the mutation settles, then
                                // clear it explicitly on both paths.
                                message.loading('Saving...', 0);

                                archiveMutation.mutate(
                                    { complianceAuditTemplateId: row.id },
                                    {
                                        onSuccess() {
                                            message.destroy();
                                            message.success('Saved.');
                                            void trpcUtils.admin.complianceAuditTemplate.invalidate();
                                        },
                                        onError(error) {
                                            message.destroy();
                                            errorMessage.show(error);
                                        },
                                    },
                                );
                            }}
                        >
                            <Button.Icon icon={<Archive />} color="red" tooltip="Archive template" />
                        </Confirm>
                    )}
                </div>
            ),
        }),
    ];

    const tableProps = usePrismaTable<TemplateRow, QueryParams['where'], TemplateOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} title="Couldn't load templates" />;
    }

    return (
        <>
            <TableToolbar
                filters={
                    <ComplianceAuditTemplateFilters
                        onChange={(where) => {
                            // The search filter's fragment travels as the top-level `search`
                            // param — the server resolves it against the REPRESENTATIVE
                            // version only (typed raw SQL), never historical versions.
                            const { search, rest } = extractRepresentativeSearch(where);
                            setInput((prev) => ({ ...prev, where: rest, search, offset: 0 }));
                        }}
                    />
                }
                actions={
                    <Space size="small">
                        <Button.Add.Link
                            to="/admin-console/platform-content/compliance-audit-templates/search/add"
                            variant="primary"
                            label="New template"
                        />
                    </Space>
                }
            />
            <PrismaTable
                {...tableProps}
                scroll={{ x: 'max-content' }}
                onRow={(record) => ({
                    className: 'cursor-pointer',
                    onClick: (e) =>
                        navigate(
                            {
                                to: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId',
                                params: { complianceAuditTemplateId: record.id },
                            },
                            e,
                        ),
                })}
            />
            <Outlet />
        </>
    );
}
